import { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import {
  MENSAJE_ACCION_KANBAN_MAX_LENGTH,
  crearConfigProgramacionKanbanDefault,
  normalizarConfigProgramacionKanban,
} from "../api/api-workflow-programacion-solicitud-producto";
import {
  ESTILO_BOTON_MODAL_ACEPTAR,
  ESTILO_BOTON_MODAL_CANCELAR,
  ESTILO_INPUT_GENERICO,
} from "../utils/const/estilos-const";

export default function ModalConfigAccionesProgramacionKanban({
  isOpen,
  column = null,
  listaKanban = [],
  listaPersonas = [],
  initialConfig = null,
  onCancel,
  onSave,
}) {
  const { t } = useTranslation();
  const acciones = useMemo(
    () =>
      (Array.isArray(column?.acciones) ? column.acciones : []).filter(
        (accion) => accion?.habilitado !== 0
      ),
    [column]
  );

  const [config, setConfig] = useState(() =>
    crearConfigProgramacionKanbanDefault({
      idKanban: column?.id ?? null,
      acciones,
    })
  );

  useEffect(() => {
    if (!isOpen || !column) return;
    setConfig(
      normalizarConfigProgramacionKanban(
        {
          ...(initialConfig ?? {}),
          idKanban: column.id,
        },
        acciones
      )
    );
  }, [isOpen, column, acciones, initialConfig]);

  if (!isOpen || !column) return null;

  const kanbanOptions = (Array.isArray(listaKanban) ? listaKanban : [])
    .filter((item) => String(item.id) !== String(column.id))
    .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0))
    .map((item) => ({
      value: item.id,
      label: item.descripcion ?? String(item.id),
    }));

  const updateAccion = (idAccion, field, value) => {
    setConfig((prev) => ({
      ...prev,
      acciones: (prev.acciones ?? []).map((accion) =>
        String(accion.idAccion) === String(idAccion)
          ? { ...accion, [field]: value }
          : accion
      ),
    }));
  };

  const canSave =
    (config.acciones ?? []).length > 0 &&
    (config.acciones ?? []).every((accion) => Boolean(accion.moverAKanban?.value));

  const selectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-gray-500 opacity-75" onClick={onCancel} />
      <div className="relative z-50 bg-white w-11/12 md:max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg shadow-xl">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900">
            {t("label_configurar_acciones_programacion_kanban")}
          </h3>
          <p className="text-sm text-gray-500 mt-1 mb-1">
            {column.descripcion ?? "—"}
          </p>
          <p className="text-sm text-gray-500 mb-4">
            {t("label_configurar_acciones_programacion_kanban_desc")}
          </p>

          <div className="space-y-4">
            {(config.acciones ?? []).length === 0 ? (
              <div className="text-sm text-gray-500 border border-dashed rounded-lg p-4 text-center">
                {t("label_sin_datos")}
              </div>
            ) : (
              (config.acciones ?? []).map((accion) => (
                <div
                  key={accion.idAccion}
                  className="border rounded-lg p-3"
                  style={{
                    borderColor: accion.color || "#e5e7eb",
                    backgroundColor: `${accion.color || "#6b7280"}12`,
                  }}
                >
                  <p
                    className="text-sm font-semibold mb-3"
                    style={{ color: accion.color || "#374151" }}
                  >
                    {accion.descripcion || accion.idAccion}
                  </p>

                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("label_mover_a_kanban")}
                  </label>
                  <Select
                    options={kanbanOptions}
                    isSearchable
                    isClearable
                    value={accion.moverAKanban}
                    onChange={(option) =>
                      updateAccion(accion.idAccion, "moverAKanban", option)
                    }
                    placeholder={t("placeholder_seleccione_kanban")}
                    classNamePrefix="react-select"
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                  <p className="text-xs text-gray-500 mt-2 mb-3">
                    {t("label_mover_a_kanban_ayuda")}
                  </p>

                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("label_mensaje_notificacion")}
                  </label>
                  <textarea
                    className={`${ESTILO_INPUT_GENERICO} min-h-[90px]`}
                    maxLength={MENSAJE_ACCION_KANBAN_MAX_LENGTH}
                    value={accion.mensaje ?? ""}
                    onChange={(event) =>
                      updateAccion(accion.idAccion, "mensaje", event.target.value)
                    }
                    placeholder={t("placeholder_mensaje_notificacion_rechazo")}
                  />
                  <p className="text-xs text-right text-gray-500 mt-1">
                    {(accion.mensaje ?? "").length}/{MENSAJE_ACCION_KANBAN_MAX_LENGTH}
                  </p>

                  <label className="block text-sm font-medium text-gray-700 mb-1 mt-3">
                    {t("label_notificar_a")}
                  </label>
                  <Select
                    options={listaPersonas}
                    isMulti
                    isSearchable
                    isClearable
                    value={accion.notificarA ?? []}
                    onChange={(options) =>
                      updateAccion(accion.idAccion, "notificarA", options || [])
                    }
                    placeholder={t("placeholder_seleccione_persona")}
                    classNamePrefix="react-select"
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                </div>
              ))
            )}
          </div>

          <div className="flex justify-end mt-6">
            <button
              type="button"
              className={ESTILO_BOTON_MODAL_CANCELAR}
              onClick={onCancel}
            >
              {t("label_cancelar")}
            </button>
            <button
              type="button"
              className={`${ESTILO_BOTON_MODAL_ACEPTAR} ${!canSave ? "opacity-50 cursor-not-allowed" : ""}`}
              disabled={!canSave}
              onClick={() =>
                onSave(
                  normalizarConfigProgramacionKanban(
                    {
                      ...config,
                      idKanban: column.id,
                    },
                    acciones
                  )
                )
              }
            >
              {t("label_guardar")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
